import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * LoLClientDashboardDemo.jsx
 * - Single-file demo UI inspired by LoL client vibe (no assets)
 * - Boot/welcome -> menu modules load -> dashboard reveal
 * - Press "R" to restart
 */

export default function LoLClientDashboardDemo() {
  const MENUS = useMemo(
    () => [
      { key: "sales", label: "Vendas", hint: "Pedidos, metas, ticket médio" },
      { key: "ar", label: "Contas a receber", hint: "Boletos, inadimplência" },
      { key: "settings", label: "Configurações", hint: "Parâmetros do sistema" },
      { key: "fiscal", label: "Emissor fiscal", hint: "NFe, NFC-e, CFOP" },
      { key: "users", label: "Usuários", hint: "Perfis, permissões, auditoria" },
    ],
    []
  );

  const [reduceMotion, setReduceMotion] = useState(false);
  const [phase, setPhase] = useState("boot"); // "boot" | "dashboard"
  const [welcomeVisible, setWelcomeVisible] = useState(true);

  const [loadedCount, setLoadedCount] = useState(0);
  const [bootProgress, setBootProgress] = useState(0);
  const [bootLines, setBootLines] = useState([]);

  const timersRef = useRef([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current.forEach((t) => clearInterval(t));
    timersRef.current = [];
  }, []);

  const reset = useCallback(() => {
    clearTimers();

    setPhase("boot");
    setWelcomeVisible(true);
    setLoadedCount(0);
    setBootProgress(0);
    setBootLines([]);

    const logScript = [
      "Sincronizando perfil…",
      "Validando permissões…",
      "Carregando módulos do lobby…",
      "Aplicando temas e fontes…",
      "Conectando serviços internos…",
      "Montando painéis operacionais…",
      "Finalizando inicialização…",
    ];

    // Logs “client-like”
    let i = 0;
    const logInterval = setInterval(() => {
      setBootLines((prev) => {
        const next = prev.slice(-7);
        const stamp = new Date().toLocaleTimeString("pt-BR", { hour12: false });
        const line = `[${stamp}] ${logScript[i] || "…"}`;
        return [...next, line];
      });
      i += 1;
      if (i > logScript.length + 2) clearInterval(logInterval);
    }, reduceMotion ? 520 : 280);
    timersRef.current.push(logInterval);

    // Progress bar
    const progInterval = setInterval(() => {
      setBootProgress((p) => {
        const step = reduceMotion ? 6 : 3.2;
        const next = Math.min(100, p + step + Math.random() * step);
        return next;
      });
    }, reduceMotion ? 220 : 140);
    timersRef.current.push(progInterval);

    // Load menu modules sequentially
    MENUS.forEach((_, idx) => {
      const t = setTimeout(() => {
        setLoadedCount((c) => Math.min(MENUS.length, c + 1));
      }, (reduceMotion ? 520 : 360) * (idx + 1) + 420);
      timersRef.current.push(t);
    });

    // Transition to dashboard
    const done = setTimeout(() => {
      setWelcomeVisible(false);
      const t2 = setTimeout(() => setPhase("dashboard"), reduceMotion ? 220 : 520);
      timersRef.current.push(t2);
    }, (reduceMotion ? 520 : 360) * MENUS.length + 1200);
    timersRef.current.push(done);

    // Cap progress at 100
    const cap = setTimeout(() => {
      setBootProgress(100);
      clearInterval(progInterval);
    }, (reduceMotion ? 520 : 360) * MENUS.length + 980);
    timersRef.current.push(cap);
  }, [MENUS, clearTimers, reduceMotion]);

  // prefers-reduced-motion
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const apply = () => setReduceMotion(Boolean(mq?.matches));
    apply();
    mq?.addEventListener?.("change", apply);
    return () => mq?.removeEventListener?.("change", apply);
  }, []);

  // mount + cleanup (StrictMode safe)
  useEffect(() => {
    reset();
    return () => clearTimers();
  }, [reset, clearTimers]);

  // Key binding: R
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key?.toLowerCase() === "r") reset();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [reset]);

  // Mock dashboard data
  const kpis = useMemo(
    () => ({
      faturamentoDia: 84250,
      pedidosDia: 128,
      ticketMedio: 658,
      recebiveisAbertos: 219400,
      inadimplencia: 3.8,
      instalacoesPendentes: 27,
      tecnicosOnline: 14,
      tempoMedioAtendimentoMin: 46,
    }),
    []
  );

  const seriesSales = useMemo(() => [12, 18, 16, 25, 22, 31, 28, 35, 32, 44, 41, 48], []);
  const seriesAR = useMemo(() => [22, 19, 17, 15, 18, 21, 20, 24], []);
  const donut = useMemo(
    () => [
      { label: "Realizados", value: 72 },
      { label: "Não realizados", value: 8 },
      { label: "Reagendados", value: 12 },
      { label: "Pendentes", value: 8 },
    ],
    []
  );

  const activity = useMemo(
    () => [
      { time: "08:12", who: "Logística", what: "Roteiros gerados", detail: "14 técnicos | 86 serviços" },
      { time: "08:27", who: "Financeiro", what: "Baixa manual", detail: "Boleto #39421 (dinheiro)" },
      { time: "09:05", who: "Vendas", what: "Pedido aprovado", detail: "Cliente: JM Distribuidora" },
      { time: "10:11", who: "Fiscal", what: "NFe emitida", detail: "NF-e 000.019.232" },
      { time: "11:03", who: "Usuários", what: "Permissão alterada", detail: "Perfil Supervisor → Auditor" },
    ],
    []
  );

  return (
    <div className="lolx-root">
      <style>{css}</style>

      {/* Background */}
      <div className="lolx-bg" aria-hidden="true">
        <div className="lolx-bgGlow" />
        <div className="lolx-bgHex" />
        <div className="lolx-bgVignette" />
        <div className={`lolx-bgShimmer ${reduceMotion ? "rm" : ""}`} />
      </div>

      {/* Top bar (LoL client-ish) */}
      <header className="lolx-topbar">
        <div className="lolx-left">
          <div className="lolx-crest" aria-hidden="true">
            <CrestIcon />
          </div>
          <div className="lolx-brand">
            <div className="lolx-appName">MYSHOP</div>
            <div className="lolx-appSub">Client Dashboard • Experimental</div>
          </div>
          <div className="lolx-divider" />
          <div className="lolx-tabs">
            <button className="lolx-tab is-active">INÍCIO</button>
            <button className="lolx-tab">OPERAÇÃO</button>
            <button className="lolx-tab">RELATÓRIOS</button>
          </div>
        </div>

        <div className="lolx-right">
          <div className="lolx-pill">
            <span className="lolx-pillKey">STATUS</span>
            <span className="lolx-pillVal">{phase === "boot" ? "SINCRONIZANDO" : "ONLINE"}</span>
          </div>
          <div className="lolx-pill">
            <span className="lolx-pillKey">ATALHO</span>
            <span className="lolx-pillVal">R = Reiniciar</span>
          </div>
          <div className="lolx-profile">
            <div className="lolx-avatar" />
            <div className="lolx-profText">
              <div className="lolx-profName">Operador</div>
              <div className="lolx-profRank">Gerência • Nível 99</div>
            </div>
          </div>
        </div>
      </header>

      <main className="lolx-main">
        {/* Welcome overlay */}
        <div className={`lolx-welcome ${welcomeVisible ? "is-on" : "is-off"} ${reduceMotion ? "rm" : ""}`}>
          <div className="lolx-welcomePanel">
            <div className="lolx-welFrame" />
            <div className={`lolx-welSheen ${reduceMotion ? "rm" : ""}`} />
            <div className="lolx-welHeader">
              <div className="lolx-welTitle">BEM-VINDO</div>
              <div className="lolx-welSub">Inicializando client • preparando módulos…</div>
            </div>

            <div className="lolx-welBody">
              <div className="lolx-welLeft">
                <div className="lolx-emblem">
                  <CrestIcon />
                </div>
                <div className="lolx-welLabel">Sincronização</div>
                <div className="lolx-welMeta">{Math.round(bootProgress)}%</div>

                <div className="lolx-loadTrack">
                  <div className="lolx-loadFill" style={{ width: `${Math.round(bootProgress)}%` }} />
                  <div className={`lolx-loadSpark ${reduceMotion ? "rm" : ""}`} />
                </div>

                <div className="lolx-welSmall">
                  <span className="lolx-muted">Módulos</span>
                  <span className="lolx-strong">
                    {loadedCount}/{MENUS.length}
                  </span>
                </div>
              </div>

              <div className="lolx-welRight">
                <div className="lolx-console">
                  {bootLines.map((line, idx) => (
                    <div key={idx} className="lolx-consoleLine">
                      <span className="lolx-dot">◆</span> {line}
                    </div>
                  ))}
                </div>

                <div className="lolx-hintRow">
                  <span className="lolx-hintTag">DICA</span>
                  <span className="lolx-muted">Pressione “R” para repetir o efeito durante a apresentação.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Menu modules */}
        <section className="lolx-section">
          <div className="lolx-sectionHeader">
            <h2 className="lolx-h2">Módulos</h2>
            <div className="lolx-muted">Sequência estilo client (ativação progressiva + brilho dourado)</div>
          </div>

          <div className="lolx-menuGrid">
            {MENUS.map((m, idx) => {
              const ready = idx < loadedCount;
              return (
                <button key={m.key} className={`lolx-card lolx-menuCard ${ready ? "is-ready" : "is-loading"}`}>
                  <div className="lolx-cardFrame" />
                  <div className={`lolx-cardSheen ${reduceMotion ? "rm" : ""}`} />
                  <div className="lolx-cardBody">
                    <div className="lolx-menuTop">
                      <span className={`lolx-seal ${ready ? "on" : ""}`} aria-hidden="true" />
                      <span className="lolx-menuLabel">{m.label}</span>
                    </div>
                    <div className="lolx-menuHint">{m.hint}</div>

                    {!ready ? (
                      <div className="lolx-skel">
                        <div className="lolx-skelLine w90" />
                        <div className="lolx-skelLine w70" />
                        <div className="lolx-skelLine w80" />
                      </div>
                    ) : (
                      <div className="lolx-readyRow">
                        <span className="lolx-badge">ATIVO</span>
                        <span className="lolx-muted">Rotas e permissões carregadas</span>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Dashboard */}
        <section className={`lolx-section lolx-dash ${phase === "dashboard" ? "is-on" : "is-off"} ${reduceMotion ? "rm" : ""}`}>
          <div className="lolx-sectionHeader">
            <h2 className="lolx-h2">Painel operacional</h2>
            <div className="lolx-muted">Cards, charts e feed — mock para demonstração</div>
          </div>

          <div className="lolx-kpiRow">
            <KpiCard title="Faturamento (dia)" value={`R$ ${formatBR(kpis.faturamentoDia)}`} sub={`${kpis.pedidosDia} pedidos • Ticket: R$ ${formatBR(kpis.ticketMedio)}`} />
            <KpiCard title="Recebíveis em aberto" value={`R$ ${formatBR(kpis.recebiveisAbertos)}`} sub={`Inadimplência: ${kpis.inadimplencia.toFixed(1)}%`} />
            <KpiCard title="Operação técnica" value={`${kpis.tecnicosOnline} técnicos online`} sub={`${kpis.instalacoesPendentes} pendências • TMA ${kpis.tempoMedioAtendimentoMin}min`} />
          </div>

          <div className="lolx-chartGrid">
            <ChartCard title="Vendas (últimas 12h)" subtitle="Tendência por janela de tempo">
              <MiniLineChart values={seriesSales} />
            </ChartCard>

            <ChartCard title="Contas a receber" subtitle="Ciclos de cobrança (simulado)">
              <BarChart values={seriesAR} />
            </ChartCard>

            <ChartCard title="Status dos serviços" subtitle="Distribuição operacional">
              <DonutChart items={donut} />
              <div className="lolx-legend">
                {donut.map((d, i) => (
                  <div key={d.label} className="lolx-legendItem">
                    <span className={`lolx-swatch s${i + 1}`} />
                    <span className="lolx-legendLabel">{d.label}</span>
                    <span className="lolx-legendVal">{d.value}%</span>
                  </div>
                ))}
              </div>
            </ChartCard>
          </div>

          <div className="lolx-bottomGrid">
            <div className="lolx-card lolx-wide">
              <div className="lolx-cardFrame" />
              <div className={`lolx-cardSheen ${reduceMotion ? "rm" : ""}`} />
              <div className="lolx-cardBody">
                <div className="lolx-cardTitleRow">
                  <div>
                    <div className="lolx-cardTitle">Feed de atividade</div>
                    <div className="lolx-cardSub">Eventos internos / auditoria (mock)</div>
                  </div>
                  <span className="lolx-badge">LIVE</span>
                </div>

                <div className="lolx-table">
                  <div className="lolx-tr lolx-th">
                    <div>Hora</div>
                    <div>Origem</div>
                    <div>Ação</div>
                    <div>Detalhe</div>
                  </div>
                  {activity.map((a, idx) => (
                    <div key={idx} className="lolx-tr">
                      <div className="lolx-mono">{a.time}</div>
                      <div>{a.who}</div>
                      <div>{a.what}</div>
                      <div className="lolx-muted">{a.detail}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lolx-card">
              <div className="lolx-cardFrame" />
              <div className={`lolx-cardSheen ${reduceMotion ? "rm" : ""}`} />
              <div className="lolx-cardBody">
                <div className="lolx-cardTitleRow">
                  <div>
                    <div className="lolx-cardTitle">Saúde do sistema</div>
                    <div className="lolx-cardSub">Indicadores técnicos (mock)</div>
                  </div>
                </div>

                <div className="lolx-health">
                  <HealthRow label="API" value="OK" />
                  <HealthRow label="Fila de eventos" value="OK" />
                  <HealthRow label="Banco de dados" value="OK" />
                  <HealthRow label="Latência média" value="37ms" />
                  <HealthRow label="Erros (1h)" value="0" />
                </div>

                <div className="lolx-muted" style={{ marginTop: 10, fontSize: 12 }}>
                  * Layout inspirado no “client vibe” (sem assets). Foco: sensação premium.
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer className="lolx-footer">
          <span className="lolx-muted">Demo UI • React + CSS • Client-style theme</span>
          <span className="lolx-muted">Aperte R para reiniciar</span>
        </footer>
      </main>
    </div>
  );
}

/* ---------- Components ---------- */

function KpiCard({ title, value, sub }) {
  return (
    <div className="lolx-card lolx-kpi">
      <div className="lolx-cardFrame" />
      <div className="lolx-cardSheen" />
      <div className="lolx-cardBody">
        <div className="lolx-kpiTitle">{title}</div>
        <div className="lolx-kpiValue">{value}</div>
        <div className="lolx-kpiSub">{sub}</div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="lolx-card lolx-chart">
      <div className="lolx-cardFrame" />
      <div className="lolx-cardSheen" />
      <div className="lolx-cardBody">
        <div className="lolx-cardTitleRow">
          <div>
            <div className="lolx-cardTitle">{title}</div>
            <div className="lolx-cardSub">{subtitle}</div>
          </div>
          <span className="lolx-badge">SIM</span>
        </div>
        <div className="lolx-chartArea">{children}</div>
      </div>
    </div>
  );
}

function HealthRow({ label, value }) {
  const ok = value === "OK" || value === "0" || value.endsWith("ms");
  return (
    <div className="lolx-healthRow">
      <span className="lolx-muted">{label}</span>
      <span className={`lolx-healthVal ${ok ? "ok" : ""}`}>{value}</span>
    </div>
  );
}

function CrestIcon() {
  return (
    <svg viewBox="0 0 64 64" className="lolx-crestSvg" aria-hidden="true">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="rgba(201,170,113,0.95)" />
          <stop offset="1" stopColor="rgba(127, 198, 255, 0.35)" />
        </linearGradient>
      </defs>
      <path
        d="M32 6c8 6 16 8 22 9-1 21-9 35-22 43C19 50 11 36 10 15c6-1 14-3 22-9z"
        fill="rgba(9,16,24,0.7)"
        stroke="url(#g)"
        strokeWidth="2"
      />
      <path d="M32 14l10 8-4 12-6 4-6-4-4-12 10-8z" fill="rgba(201,170,113,0.16)" stroke="rgba(201,170,113,0.6)" strokeWidth="1.5" />
      <path d="M32 19l5 4-2 7-3 2-3-2-2-7 5-4z" fill="rgba(127,198,255,0.12)" stroke="rgba(127,198,255,0.35)" strokeWidth="1" />
    </svg>
  );
}

/* ---------- SVG Charts (no libs) ---------- */

function MiniLineChart({ values }) {
  const w = 520,
    h = 160,
    pad = 14;
  const min = Math.min(...values);
  const max = Math.max(...values);

  const points = values
    .map((v, i) => {
      const x = pad + (i * (w - pad * 2)) / (values.length - 1);
      const t = (v - min) / (max - min || 1);
      const y = h - pad - t * (h - pad * 2);
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  return (
    <svg className="lolx-svg" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Linha simulada">
      <defs>
        <linearGradient id="lolLine" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="rgba(127,198,255,0.25)" />
          <stop offset="0.6" stopColor="rgba(201,170,113,0.85)" />
          <stop offset="1" stopColor="rgba(201,170,113,0.25)" />
        </linearGradient>
      </defs>
      <GridLines w={w} h={h} pad={pad} />

      <polyline className="lolx-lineShadow" points={points} />
      <polyline className="lolx-line" points={points} style={{ stroke: "url(#lolLine)" }} />

      {values.map((v, i) => {
        const x = pad + (i * (w - pad * 2)) / (values.length - 1);
        const t = (v - min) / (max - min || 1);
        const y = h - pad - t * (h - pad * 2);
        return <circle key={i} cx={x} cy={y} r="3" className="lolx-dotPt" />;
      })}
    </svg>
  );
}

function BarChart({ values }) {
  const w = 520,
    h = 160,
    pad = 14;
  const max = Math.max(...values);
  const bw = (w - pad * 2) / values.length;

  return (
    <svg className="lolx-svg" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Barras simuladas">
      <GridLines w={w} h={h} pad={pad} />
      {values.map((v, i) => {
        const t = v / (max || 1);
        const barH = t * (h - pad * 2);
        const x = pad + i * bw + bw * 0.15;
        const y = h - pad - barH;
        const width = bw * 0.7;
        return (
          <g key={i}>
            <rect className="lolx-barShadow" x={x} y={y + 2} width={width} height={barH} rx="6" />
            <rect className="lolx-bar" x={x} y={y} width={width} height={barH} rx="6" />
          </g>
        );
      })}
    </svg>
  );
}

function DonutChart({ items }) {
  const size = 220;
  const r = 78;
  const cx = size / 2;
  const cy = size / 2;
  const total = items.reduce((s, it) => s + it.value, 0) || 1;
  const C = 2 * Math.PI * r;

  let acc = 0;
  return (
    <div className="lolx-donutWrap">
      <svg className="lolx-donut" viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Donut simulada">
        <circle cx={cx} cy={cy} r={r} className="lolx-donutTrack" />
        {items.map((it, idx) => {
          const frac = it.value / total;
          const dash = frac * C;
          const gap = C - dash;
          const offset = C * (1 - acc);
          acc += frac;
          return (
            <circle
              key={it.label}
              cx={cx}
              cy={cy}
              r={r}
              className={`lolx-donutSeg s${idx + 1}`}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={offset}
            />
          );
        })}
        <circle cx={cx} cy={cy} r={r - 18} className="lolx-donutHole" />
        <text x={cx} y={cy - 6} textAnchor="middle" className="lolx-donutTextBig">
          {items[0]?.value ?? 0}%
        </text>
        <text x={cx} y={cy + 18} textAnchor="middle" className="lolx-donutTextSmall">
          realizados
        </text>
      </svg>
    </div>
  );
}

function GridLines({ w, h, pad }) {
  const lines = 5;
  const ys = Array.from({ length: lines }, (_, i) => pad + (i * (h - pad * 2)) / (lines - 1));
  return (
    <g>
      {ys.map((y, i) => (
        <line key={i} x1={pad} y1={y} x2={w - pad} y2={y} className="lolx-gridLine" />
      ))}
      <line x1={pad} y1={pad} x2={pad} y2={h - pad} className="lolx-gridLine2" />
      <line x1={w - pad} y1={pad} x2={w - pad} y2={h - pad} className="lolx-gridLine2" />
    </g>
  );
}

/* ---------- Helpers ---------- */

function formatBR(n) {
  const s = String(Math.round(n));
  const parts = [];
  for (let i = s.length; i > 0; i -= 3) {
    const start = Math.max(0, i - 3);
    parts.unshift(s.slice(start, i));
  }
  return parts.join(".");
}

/* ---------- CSS ---------- */

const css = `
:root{
  --lol-bg0:#06090f;
  --lol-bg1:#0b0f18;
  --lol-panel: rgba(9, 14, 22, .78);
  --lol-panel2: rgba(12, 18, 28, .72);
  --lol-border: rgba(201,170,113,.35);
  --lol-border2: rgba(127,198,255,.18);
  --lol-gold: rgb(201,170,113);
  --lol-goldSoft: rgba(201,170,113,.25);
  --lol-ice: rgb(127,198,255);
  --lol-iceSoft: rgba(127,198,255,.18);
  --lol-text: rgba(242, 236, 225, .95);
  --lol-muted: rgba(242, 236, 225, .65);
  --lol-shadow: rgba(0,0,0,.55);
  --lol-radius: 16px;
}

*{box-sizing:border-box}
.lolx-root{
  min-height:100vh;
  color: var(--lol-text);
  background: linear-gradient(180deg, var(--lol-bg0), var(--lol-bg1));
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
}

/* Background layers */
.lolx-bg{position:fixed; inset:0; pointer-events:none; overflow:hidden}
.lolx-bgGlow{
  position:absolute; inset:-30%;
  background:
    radial-gradient(900px 700px at 20% 20%, rgba(127,198,255,.10), transparent 55%),
    radial-gradient(900px 700px at 80% 60%, rgba(201,170,113,.12), transparent 55%),
    radial-gradient(700px 500px at 45% 95%, rgba(201,170,113,.06), transparent 60%);
  filter: blur(1px);
}
.lolx-bgHex{
  position:absolute; inset:-20%;
  background:
    linear-gradient(to right, rgba(201,170,113,.04) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(201,170,113,.03) 1px, transparent 1px);
  background-size: 52px 52px;
  transform: rotate(8deg) scale(1.05);
  opacity:.55;
  mask-image: radial-gradient(circle at 40% 35%, black 35%, transparent 75%);
}
.lolx-bgVignette{
  position:absolute; inset:0;
  background: radial-gradient(circle at 50% 35%, transparent 30%, rgba(0,0,0,.72) 80%);
}
.lolx-bgShimmer{
  position:absolute; inset:-30% -30%;
  background: linear-gradient(115deg, transparent, rgba(201,170,113,.10), transparent);
  transform: translateX(-40%);
  animation: lolxShimmer 8s linear infinite;
  opacity:.35;
}
.lolx-bgShimmer.rm{animation:none}
@keyframes lolxShimmer{
  0%{transform: translateX(-40%)}
  100%{transform: translateX(40%)}
}

/* Topbar */
.lolx-topbar{
  position:sticky; top:0; z-index:10;
  display:flex; justify-content:space-between; align-items:center;
  padding: 14px 18px;
  background: linear-gradient(180deg, rgba(5,8,13,.92), rgba(5,8,13,.55));
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(201,170,113,.18);
}
.lolx-left{display:flex; align-items:center; gap:12px; min-width:0}
.lolx-crest{
  width:36px; height:36px;
  display:grid; place-items:center;
  border-radius: 10px;
  background: rgba(10,16,24,.65);
  border: 1px solid rgba(201,170,113,.25);
  box-shadow: 0 10px 28px rgba(0,0,0,.35);
}
.lolx-crestSvg{width:30px; height:30px}
.lolx-brand{display:flex; flex-direction:column; line-height:1.1}
.lolx-appName{
  font-weight:900; letter-spacing:.22em; font-size:12px;
  color: rgba(242,236,225,.95);
}
.lolx-appSub{font-size:12px; color: var(--lol-muted)}
.lolx-divider{
  width:1px; height:22px;
  background: rgba(201,170,113,.18);
  margin:0 4px;
}
.lolx-tabs{display:flex; gap:8px}
.lolx-tab{
  border:1px solid rgba(201,170,113,.18);
  background: rgba(10,16,24,.45);
  color: rgba(242,236,225,.8);
  padding: 8px 10px;
  border-radius: 12px;
  letter-spacing:.12em;
  font-size:11px;
  cursor:pointer;
}
.lolx-tab.is-active{
  border-color: rgba(201,170,113,.38);
  box-shadow: 0 0 0 1px rgba(127,198,255,.10) inset, 0 10px 28px rgba(0,0,0,.35);
  background: linear-gradient(180deg, rgba(201,170,113,.12), rgba(10,16,24,.55));
}
.lolx-right{display:flex; align-items:center; gap:10px}
.lolx-pill{
  display:flex; gap:8px; align-items:center;
  padding:8px 10px;
  border-radius: 999px;
  border:1px solid rgba(201,170,113,.18);
  background: rgba(10,16,24,.45);
}
.lolx-pillKey{font-size:11px; letter-spacing:.14em; color: var(--lol-muted)}
.lolx-pillVal{font-size:11px; font-weight:900; letter-spacing:.10em}
.lolx-profile{display:flex; align-items:center; gap:10px; padding-left:6px}
.lolx-avatar{
  width:34px; height:34px; border-radius: 12px;
  background:
    radial-gradient(circle at 30% 30%, rgba(127,198,255,.25), transparent 55%),
    radial-gradient(circle at 70% 70%, rgba(201,170,113,.22), transparent 55%),
    rgba(10,16,24,.65);
  border:1px solid rgba(201,170,113,.22);
}
.lolx-profText{display:flex; flex-direction:column; line-height:1.1}
.lolx-profName{font-weight:900; letter-spacing:.08em; font-size:12px}
.lolx-profRank{font-size:11px; color: var(--lol-muted)}

.lolx-main{
  max-width: 1260px;
  margin: 0 auto;
  padding: 18px 18px 28px;
}

/* Section */
.lolx-section{margin-top:16px}
.lolx-sectionHeader{display:flex; align-items:baseline; justify-content:space-between; gap:10px; margin-bottom:12px}
.lolx-h2{
  margin:0;
  font-size:14px;
  letter-spacing:.18em;
  font-weight:900;
  text-transform:uppercase;
}
.lolx-muted{color: var(--lol-muted)}
.lolx-strong{font-weight:900; letter-spacing:.08em}
.lolx-mono{font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace}

/* Welcome overlay */
.lolx-welcome{position:fixed; inset:0; z-index:20; display:flex; align-items:center; justify-content:center; pointer-events:none}
.lolx-welcome.is-on{opacity:1; transition: opacity .55s ease}
.lolx-welcome.is-off{opacity:0; transition: opacity .55s ease}
.lolx-welcome.rm{transition:none}
.lolx-welcomePanel{
  width: min(900px, calc(100% - 34px));
  border-radius: 20px;
  padding: 18px;
  background: rgba(8, 12, 18, .72);
  border: 1px solid rgba(201,170,113,.22);
  box-shadow: 0 26px 90px rgba(0,0,0,.7);
  position:relative;
  overflow:hidden;
}
.lolx-welFrame{
  position:absolute; inset:0;
  background:
    radial-gradient(680px 220px at 15% 0%, rgba(127,198,255,.10), transparent 60%),
    radial-gradient(650px 240px at 85% 100%, rgba(201,170,113,.14), transparent 62%);
  opacity:.95;
}
.lolx-welSheen{
  position:absolute; inset:-35% -30%;
  background: linear-gradient(110deg, transparent, rgba(201,170,113,.16), transparent);
  transform: translateX(-55%);
  animation: lolxSheen 2.8s ease-in-out infinite;
  opacity:.5;
}
.lolx-welSheen.rm{animation:none}
@keyframes lolxSheen{0%{transform:translateX(-55%)}100%{transform:translateX(55%)}}

.lolx-welHeader{position:relative; display:flex; justify-content:space-between; align-items:flex-start; gap:12px}
.lolx-welTitle{
  font-weight:900;
  letter-spacing:.28em;
  text-transform:uppercase;
  font-size:14px;
}
.lolx-welSub{margin-top:6px; font-size:12px; color: var(--lol-muted)}

.lolx-welBody{
  position:relative;
  margin-top: 14px;
  display:grid;
  grid-template-columns: 320px 1fr;
  gap: 14px;
}
@media (max-width: 860px){
  .lolx-welBody{grid-template-columns: 1fr}
}

.lolx-welLeft{
  border:1px solid rgba(201,170,113,.16);
  background: rgba(10,16,24,.42);
  border-radius: 16px;
  padding: 14px;
}
.lolx-emblem{
  width:54px; height:54px;
  display:grid; place-items:center;
  border-radius: 16px;
  border:1px solid rgba(201,170,113,.22);
  background: rgba(9,14,22,.55);
  box-shadow: 0 10px 28px rgba(0,0,0,.35);
}
.lolx-welLabel{
  margin-top:12px;
  font-size:12px;
  letter-spacing:.16em;
  text-transform:uppercase;
  color: rgba(242,236,225,.85);
}
.lolx-welMeta{
  margin-top:6px;
  font-size:22px;
  font-weight:900;
  letter-spacing:.06em;
}
.lolx-loadTrack{
  margin-top:10px;
  height: 12px;
  border-radius: 999px;
  border:1px solid rgba(201,170,113,.22);
  background: rgba(0,0,0,.35);
  overflow:hidden;
  position:relative;
}
.lolx-loadFill{
  height:100%;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(201,170,113,.25), rgba(201,170,113,.92), rgba(127,198,255,.18));
  box-shadow: 0 0 18px rgba(201,170,113,.22);
}
.lolx-loadSpark{
  position:absolute; inset:-120% -40%;
  background: linear-gradient(90deg, transparent, rgba(242,236,225,.18), transparent);
  transform: translateX(-50%);
  animation: spark 1.2s linear infinite;
  opacity:.65;
}
.lolx-loadSpark.rm{animation:none}
@keyframes spark{0%{transform:translateX(-50%)}100%{transform:translateX(50%)}}

.lolx-welSmall{margin-top:10px; display:flex; justify-content:space-between; font-size:12px}

.lolx-welRight{
  border:1px solid rgba(127,198,255,.12);
  background: rgba(10,16,24,.32);
  border-radius: 16px;
  padding: 14px;
}
.lolx-console{
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size:12px;
  color: rgba(242,236,225,.82);
  line-height:1.5;
}
.lolx-consoleLine{opacity:.95; margin:5px 0}
.lolx-dot{color: rgba(201,170,113,.85)}
.lolx-hintRow{margin-top:10px; display:flex; align-items:center; gap:10px}
.lolx-hintTag{
  font-size:11px;
  letter-spacing:.16em;
  text-transform:uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  border:1px solid rgba(201,170,113,.22);
  background: rgba(201,170,113,.10);
  color: rgba(242,236,225,.92);
}

/* Menu grid */
.lolx-menuGrid{
  display:grid;
  grid-template-columns: repeat(5, minmax(190px, 1fr));
  gap: 12px;
}
@media (max-width: 1200px){ .lolx-menuGrid{grid-template-columns: repeat(2, minmax(220px, 1fr))} }
@media (max-width: 560px){ .lolx-menuGrid{grid-template-columns: 1fr} }

.lolx-card{
  position:relative;
  border-radius: var(--lol-radius);
  background: var(--lol-panel);
  border: 1px solid rgba(201,170,113,.18);
  box-shadow: 0 16px 60px rgba(0,0,0,.55);
  overflow:hidden;
}
.lolx-cardFrame{
  position:absolute; inset:0;
  background:
    linear-gradient(180deg, rgba(201,170,113,.10), transparent 45%),
    radial-gradient(600px 140px at 15% 0%, rgba(127,198,255,.10), transparent 55%),
    radial-gradient(520px 160px at 85% 100%, rgba(201,170,113,.12), transparent 58%);
  pointer-events:none;
}
.lolx-cardSheen{
  position:absolute; inset:-35% -30%;
  background: linear-gradient(110deg, transparent, rgba(201,170,113,.14), transparent);
  transform: translateX(-60%);
  animation: cardSheen 3.1s ease-in-out infinite;
  opacity:.55;
  pointer-events:none;
}
.lolx-cardSheen.rm{animation:none}
@keyframes cardSheen{0%{transform:translateX(-60%)}100%{transform:translateX(60%)}}

.lolx-cardBody{position:relative; padding:14px}
.lolx-menuCard{
  text-align:left;
  width:100%;
  cursor:pointer;
  transform: translateY(6px);
  opacity:.78;
  background: var(--lol-panel2);
}
.lolx-menuCard.is-ready{
  transform: translateY(0);
  opacity:1;
  border-color: rgba(201,170,113,.30);
  box-shadow: 0 16px 80px rgba(201,170,113,.08), 0 16px 60px rgba(0,0,0,.55);
  animation: pop .45s ease-out both;
}
@keyframes pop{from{transform:translateY(10px) scale(.985); opacity:.65}to{transform:translateY(0) scale(1); opacity:1}}

.lolx-menuTop{display:flex; align-items:center; gap:10px}
.lolx-seal{
  width:12px; height:12px; border-radius:4px;
  border:1px solid rgba(201,170,113,.25);
  background: rgba(201,170,113,.06);
}
.lolx-seal.on{
  background: rgba(201,170,113,.85);
  box-shadow: 0 0 16px rgba(201,170,113,.35);
}
.lolx-menuLabel{font-weight:900; letter-spacing:.12em; text-transform:uppercase; font-size:12px}
.lolx-menuHint{margin-top:6px; font-size:12px; color: var(--lol-muted)}

.lolx-badge{
  display:inline-flex; align-items:center; justify-content:center;
  padding:4px 10px;
  border-radius:999px;
  border:1px solid rgba(201,170,113,.25);
  background: rgba(201,170,113,.10);
  color: rgba(242,236,225,.92);
  font-size:11px;
  font-weight:900;
  letter-spacing:.14em;
}
.lolx-readyRow{margin-top:10px; display:flex; align-items:center; gap:10px}

.lolx-skel{margin-top:10px; display:grid; gap:8px}
.lolx-skelLine{
  height:10px;
  border-radius:999px;
  background: linear-gradient(90deg, rgba(201,170,113,.06), rgba(201,170,113,.16), rgba(201,170,113,.06));
  position:relative;
  overflow:hidden;
}
.lolx-skelLine::after{
  content:"";
  position:absolute; inset:0;
  background: linear-gradient(90deg, transparent, rgba(242,236,225,.14), transparent);
  transform: translateX(-60%);
  animation: skel 1.5s ease-in-out infinite;
}
@keyframes skel{0%{transform:translateX(-60%)}100%{transform:translateX(60%)}}
.w90{width:90%}.w80{width:80%}.w70{width:70%}

/* Dashboard reveal */
.lolx-dash.is-off{opacity:0; transform: translateY(8px); pointer-events:none}
.lolx-dash.is-on{opacity:1; transform: translateY(0); transition: opacity .7s ease, transform .7s ease}
.lolx-dash.rm{transition:none}

/* KPI + charts layout */
.lolx-kpiRow{
  display:grid;
  grid-template-columns: repeat(3, minmax(240px, 1fr));
  gap:12px;
}
@media (max-width: 1100px){ .lolx-kpiRow{grid-template-columns: 1fr} }

.lolx-kpiTitle{
  font-size:12px;
  letter-spacing:.16em;
  text-transform:uppercase;
  color: rgba(242,236,225,.74);
}
.lolx-kpiValue{
  margin-top:6px;
  font-size:22px;
  font-weight:900;
  letter-spacing:.06em;
}
.lolx-kpiSub{margin-top:6px; font-size:12px; color: var(--lol-muted)}

.lolx-chartGrid{
  margin-top:12px;
  display:grid;
  grid-template-columns: 1.2fr 1.2fr 1fr;
  gap:12px;
}
@media (max-width: 1200px){ .lolx-chartGrid{grid-template-columns: 1fr} }

.lolx-cardTitleRow{display:flex; align-items:flex-start; justify-content:space-between; gap:10px}
.lolx-cardTitle{
  font-size:12px;
  font-weight:900;
  letter-spacing:.18em;
  text-transform:uppercase;
}
.lolx-cardSub{margin-top:4px; font-size:12px; color: var(--lol-muted)}
.lolx-chartArea{margin-top:10px}

.lolx-svg{
  width:100%;
  height:auto;
  display:block;
  border-radius: 14px;
  border:1px solid rgba(201,170,113,.16);
  background: rgba(0,0,0,.22);
}
.lolx-gridLine{stroke: rgba(201,170,113,.10); stroke-width:1}
.lolx-gridLine2{stroke: rgba(127,198,255,.06); stroke-width:1}

.lolx-lineShadow{fill:none; stroke: rgba(0,0,0,.55); stroke-width:6; stroke-linecap:round; stroke-linejoin:round}
.lolx-line{
  fill:none;
  stroke-width:3.2;
  stroke-linecap:round;
  stroke-linejoin:round;
  stroke-dasharray: 1200;
  stroke-dashoffset: 1200;
  animation: draw 1.15s ease-out forwards;
}
@keyframes draw{to{stroke-dashoffset:0}}
.lolx-dotPt{fill: rgba(201,170,113,.88); stroke: rgba(0,0,0,.25); stroke-width:1}

.lolx-barShadow{fill: rgba(0,0,0,.45)}
.lolx-bar{fill: rgba(201,170,113,.55); stroke: rgba(201,170,113,.22); stroke-width:1}

.lolx-donutWrap{display:flex; justify-content:center}
.lolx-donut{width:220px; height:220px}
.lolx-donutTrack{fill:none; stroke: rgba(201,170,113,.10); stroke-width:18}
.lolx-donutSeg{
  fill:none;
  stroke-width:18;
  transform-origin: 110px 110px;
  transform: rotate(-90deg);
  stroke-linecap: round;
}
.lolx-donutHole{fill: rgba(10,16,24,.78)}
.lolx-donutTextBig{font-size:22px; font-weight:900; fill: rgba(242,236,225,.92); letter-spacing:.06em}
.lolx-donutTextSmall{font-size:12px; font-weight:700; fill: rgba(242,236,225,.65); letter-spacing:.14em; text-transform:uppercase}

.lolx-legend{margin-top:10px; display:grid; gap:8px}
.lolx-legendItem{display:flex; align-items:center; justify-content:space-between; gap:10px}
.lolx-swatch{width:12px; height:12px; border-radius:4px; border:1px solid rgba(255,255,255,.12)}
.lolx-legendLabel{font-size:12px; color: rgba(242,236,225,.85)}
.lolx-legendVal{font-size:12px; font-weight:900; letter-spacing:.06em}

.s1{stroke: rgba(201,170,113,.92)}
.s2{stroke: rgba(127,198,255,.55)}
.s3{stroke: rgba(242,236,225,.40)}
.s4{stroke: rgba(201,170,113,.32)}
.lolx-swatch.s1{background: rgba(201,170,113,.92)}
.lolx-swatch.s2{background: rgba(127,198,255,.55)}
.lolx-swatch.s3{background: rgba(242,236,225,.40)}
.lolx-swatch.s4{background: rgba(201,170,113,.32)}

/* Bottom grid */
.lolx-bottomGrid{
  margin-top:12px;
  display:grid;
  grid-template-columns: 1.6fr 1fr;
  gap:12px;
}
@media (max-width: 1200px){ .lolx-bottomGrid{grid-template-columns: 1fr} }
.lolx-wide{min-height:260px}

.lolx-table{margin-top:10px; border-radius:14px; overflow:hidden; border:1px solid rgba(201,170,113,.14)}
.lolx-tr{
  display:grid;
  grid-template-columns: 90px 140px 1fr 1.2fr;
  gap:10px;
  padding:10px 12px;
  background: rgba(0,0,0,.18);
  border-bottom:1px solid rgba(201,170,113,.10);
}
.lolx-tr:last-child{border-bottom:none}
.lolx-th{
  background: rgba(201,170,113,.07);
  color: rgba(242,236,225,.82);
  font-weight:900;
  letter-spacing:.16em;
  text-transform:uppercase;
  font-size:11px;
}

.lolx-health{margin-top:10px; display:grid; gap:10px}
.lolx-healthRow{
  display:flex; justify-content:space-between; align-items:center;
  padding:10px 12px;
  border-radius:14px;
  border:1px solid rgba(201,170,113,.12);
  background: rgba(0,0,0,.18);
}
.lolx-healthVal{font-weight:900; letter-spacing:.08em}
.lolx-healthVal.ok{color: rgba(201,170,113,.95); text-shadow: 0 0 16px rgba(201,170,113,.18)}

.lolx-footer{
  margin-top: 14px;
  padding: 12px 2px 0;
  display:flex;
  justify-content:space-between;
  border-top: 1px solid rgba(201,170,113,.12);
}
`;
